import React, { useState, useEffect } from 'react';
import { FiSearch, FiEye, FiDownload, FiTrash2, FiEdit2, FiInfo, FiFileText, FiVideo, FiLink, FiAlertTriangle } from 'react-icons/fi';
import DocumentationService from '../../services/DocumentationService';
import toast from 'react-hot-toast';
import Select from 'react-select';

// Reusable Pagination Component (matching TicketTable style)
const CustomPagination = ({ currentPage, totalCount, limit, onChangePage }) => {
  if (!totalCount || totalCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const start = (currentPage - 1) * limit + 1;
  const end = Math.min(currentPage * limit, totalCount);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    startPage = Math.max(1, startPage);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
      <div className="text-xs text-gray-600">
        Showing <span className="font-semibold">{start}-{end}</span> of <span className="font-semibold">{totalCount}</span>
      </div>
      <div className="flex gap-1 items-center">
        <button
          onClick={() => onChangePage(1)}
          disabled={currentPage <= 1}
          className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          First
        </button>
        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => onChangePage(pageNum)}
            className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs border rounded font-medium transition-all ${
              currentPage === pageNum ? "bg-red-600 text-white border-red-600 shadow-sm" : "border-gray-300 hover:bg-gray-100 bg-white text-gray-700"
            }`}
          >
            {pageNum}
          </button>
        ))}
        <button
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          Last
        </button>
      </div>
    </div>
  );
};

function DocumentationList({ refreshTrigger, onPreview, onEdit, isAdmin, canEdit }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [docTypes, setDocTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#ef4444' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 1px #ef4444' : 'none',
      borderRadius: '0.5rem',
      padding: '0',
      minHeight: '38px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '13px',
      "&:hover": { borderColor: state.isFocused ? '#ef4444' : '#d1d5db' }
    }),
    option: (base, state) => ({
       ...base,
       cursor: 'pointer',
       backgroundColor: state.isSelected ? '#ef4444' : state.isFocused ? '#fef2f2' : 'transparent',
       color: state.isSelected ? 'white' : '#374151',
       fontSize: '13px',
       "&:active": { backgroundColor: '#ef4444', color: 'white' }
    }),
    singleValue: (base) => ({ ...base, fontSize: '13px', color: '#374151' }),
    placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
    menu: (base) => ({
       ...base, zIndex: 50, borderRadius: '0.5rem',
       boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    })
  };

  const limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 9999, label: 'All' }
  ];

  const customerOptions = [
    { value: '', label: 'All Customers' },
    ...customers.map(c => ({
      value: String(c.child_id),
      label: c.short_code ? `${c.short_code} - ${c.customer_name}` : c.customer_name
    }))
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    ...docTypes.map(t => ({ value: t.type_name, label: t.type_name }))
  ];

  useEffect(() => {
    fetchDocs();
  }, [refreshTrigger, search, typeFilter, customerFilter, page, limit]);

  useEffect(() => {
    fetchDocTypes();
    fetchCustomers();
  }, [refreshTrigger]);

  const fetchCustomers = async () => {
    try {
      const resp = await DocumentationService.getCustomers();
      if (resp.status) {
        setCustomers(resp.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDocTypes = async () => {
    try {
      const resp = await DocumentationService.getDocTypes();
      if (Array.isArray(resp)) {
        setDocTypes(resp);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const resp = await DocumentationService.getDocs({
        search,
        type: typeFilter,
        customer: customerFilter,
        offset: (page - 1) * limit,
        limit
      });
      if (resp.status) {
        setDocs(resp.data);
        setTotal(resp.total);
      }
    } catch (e) {
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (doc) => {
    if (!canEdit) return;
    const newStatus = doc.status === 'Active' ? 'Inactive' : 'Active';
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      const resp = await DocumentationService.updateDoc(doc.id, formData);
      if (resp.status) {
        toast.success(`Status updated to ${newStatus}`);
        fetchDocs();
      } else {
         toast.error(resp.message || 'Failed to update status');
      }
    } catch (e) {
      toast.error('Error updating status');
    }
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    try {
      const resp = await DocumentationService.deleteDoc(docToDelete.id);
      if (resp.status) {
        toast.success(resp.message || 'Documentation deleted successfully');
        setShowDeleteConfirm(false);
        setDocToDelete(null);
        fetchDocs();
      } else {
         toast.error(resp.message || 'Error occurred while deleting');
      }
    } catch (e) {
      toast.error('Failed to delete documentation');
    }
  };

  return (
    <div className="flex flex-col">
      {/* Filters Bar */}
      <div className="p-4 bg-white border-b flex flex-col md:flex-row items-center justify-end gap-4">
        {/* Left spacing for desktop, hidden on mobile */}
        <div className="hidden md:flex flex-1"></div>
        
        <div className="flex flex-wrap items-center justify-center md:justify-end gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-tight">Show</span>
            <div className="w-24">
              <Select
                options={limitOptions}
                value={limitOptions.find(opt => opt.value === limit)}
                onChange={(selected) => { setLimit(selected?.value || 5); setPage(1); }}
                styles={customSelectStyles}
                isSearchable={false}
              />
            </div>
          </div>

          <div className="w-[1px] h-6 bg-gray-200 mx-1 hidden sm:block"></div>
          
          <div className="w-full sm:w-56">
            <Select
              options={customerOptions}
              value={customerOptions.find(opt => opt.value === customerFilter)}
              onChange={(selected) => { setCustomerFilter(selected?.value || ''); setPage(1); }}
              styles={customSelectStyles}
              placeholder="All Customers"
              isSearchable
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={typeOptions}
              value={typeOptions.find(opt => opt.value === typeFilter)}
              onChange={(selected) => { setTypeFilter(selected?.value || ''); setPage(1); }}
              styles={customSelectStyles}
              placeholder="All Types"
              isSearchable
            />
          </div>

          <div className="relative w-full md:w-64">
            <input 
              type="text" 
              placeholder="Search title, module..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-all shadow-sm"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 font-semibold uppercase text-[11px] tracking-wider border-b">
              <th className="px-5 py-4 text-center w-16">S.No</th>
              <th className="px-5 py-4 text-left">Title / Module</th>
              <th className="px-5 py-4 text-center">Doc Type</th>
              <th className="px-5 py-4 text-left">Uploader</th>
              <th className="px-5 py-4 text-center">Version</th>
              <th className="px-5 py-4 text-center">Resources</th>
              <th className="px-5 py-4 text-center">Created Date</th>
              <th className="px-5 py-4 text-center w-24">Status</th>
              <th className="px-5 py-4 text-right w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
               <tr><td colSpan="9" className="p-12 text-center text-gray-500">Loading documents...</td></tr>
            ) : docs.length === 0 ? (
               <tr><td colSpan="9" className="p-12 text-center text-gray-400 italic">No documentation found.</td></tr>
            ) : docs.map((doc, index) => (
              <tr key={doc.id} className="hover:bg-gray-50/50 transition-colors group">
                <td className="px-5 py-4 text-center text-gray-500 font-medium">{(page - 1) * limit + index + 1}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-800 text-[13px]">{doc.title}</span>
                    <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] w-fit mt-1 uppercase font-semibold">
                      {doc.module}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-4 text-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase w-fit ${
                      doc.doc_type === 'Implementation' ? 'bg-indigo-100 text-indigo-700' :
                      doc.doc_type === 'Help Video' ? 'bg-orange-100 text-orange-700' :
                      doc.doc_type === 'Product Documentation' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {doc.doc_type}
                    </span>
                    
                    {doc.customers && (
                      <div className="flex flex-wrap justify-center gap-1 max-w-[150px]">
                        {doc.customers.split(',').filter(id => id.trim()).map((cId) => {
                          const cust = customers.find((c) => String(c.child_id) === String(cId.trim()));
                          if (!cust) return null;
                          return (
                            <span 
                              key={cId} 
                              className="px-1.5 py-[2px] bg-red-50 text-red-600 border border-red-100 rounded text-[9px] font-bold tracking-wider shadow-sm" 
                              title={cust.customer_name}
                            >
                              {cust.short_code || `Cust ${cId}`}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-700 text-[13px]">{doc.uploaded_by_name || 'System'}</td>
                <td className="px-5 py-4 text-center font-bold text-gray-700">{doc.version || '1.0'}</td>
                <td className="px-5 py-4 text-center">
                   <div className="flex items-center justify-center gap-2">
                      {doc.video_url && (
                        <div title="Has Video Link" className="w-7 h-7 bg-red-50 text-red-500 rounded-full flex items-center justify-center border border-red-100 shadow-sm">
                          <FiVideo className="text-sm" />
                        </div>
                      )}
                      {(doc.attachment_count > 0 || doc.attachments?.length > 0) && (
                        <div title={`${doc.attachment_count} File(s)`} className="w-7 h-7 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center border border-blue-100 shadow-sm font-bold text-[10px]">
                           {doc.attachment_count || doc.attachments?.length}
                        </div>
                      )}
                      {!doc.video_url && !doc.attachment_count && (
                        <span className="text-gray-300">-</span>
                      )}
                   </div>
                </td>
                <td className="px-5 py-4 text-center text-gray-500 text-[13px] whitespace-nowrap">
                   {new Date(doc.created_at).toLocaleDateString('en-GB')} <br/>
                   <span className="text-[10px] opacity-75">{new Date(doc.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </td>
                <td className="px-5 py-4 text-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <div 
                      onClick={() => handleStatusToggle(doc)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${!canEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${doc.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'}`}
                      title={canEdit ? 'Toggle Status' : ''}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${doc.status === 'Active' ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                    <span className={`text-[10px] font-bold leading-none ${doc.status === 'Active' ? 'text-green-700' : 'text-gray-500'}`}>
                      {doc.status === 'Active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => onPreview(doc)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                      title="View Preview"
                    >
                      <FiEye className="text-lg" />
                    </button>
                    
                    {canEdit && (
                      <button 
                        onClick={() => onEdit(doc)}
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-100" 
                        title="Edit Record"
                      >
                        <FiEdit2 className="text-lg" />
                      </button>
                    )}

                    {isAdmin && (
                      <button 
                        onClick={() => {
                          setDocToDelete(doc);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100" 
                        title="Delete Record"
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CustomPagination 
        currentPage={page} 
        totalCount={total} 
        limit={limit} 
        onChangePage={setPage} 
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] animate-in fade-in duration-200 backdrop-blur-[1px]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center gap-3 mb-4">
               <div className="bg-red-100 p-2 rounded-full">
                  <FiAlertTriangle className="text-red-600 text-xl" />
               </div>
               <h3 className="text-lg font-bold text-gray-900">Delete Documentation</h3>
            </div>
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
              Are you sure you want to delete <span className="font-bold text-gray-800">"{docToDelete?.title}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDocToDelete(null);
                }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-sm shadow-md shadow-red-100 transition-all active:scale-95"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentationList;
